import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { explorerUrl } from '@/lib/format';
import type { Hex } from '@/lib/demo/types';
import { IconButton } from './button';
import { Spinner } from './feedback';
import { AlertTriangle, ArrowUpRight, Check, Info, X } from './icons';

/* ────────────────────────────────────────────────────────────────────────────
   Toasts.

   One polite live region in the top-right. Transaction toasts carry an explorer
   link; `pending` never auto-dismisses (the chain decides when it's done) and
   hovering the stack pauses every countdown so a link is always clickable.
   ──────────────────────────────────────────────────────────────────────────── */

export type ToastTone = 'success' | 'error' | 'warn' | 'info' | 'pending';

export interface ToastInput {
  tone?: ToastTone;
  title: string;
  description?: ReactNode;
  txHash?: Hex;
  chainId?: number;
  /** ms. Defaults to 5000; ignored for `pending`. */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

export interface ToastRecord extends ToastInput {
  id: string;
}

export interface ToastApi {
  push: (t: ToastInput) => string;
  dismiss: (id: string) => void;
}

/** Never show more than this many at once — the rest queue behind. */
const MAX_VISIBLE = 4;
const DEFAULT_DURATION = 5000;

const ToastContext = createContext<ToastApi | null>(null);

let counter = 0;
const nextId = () => `toast-${Date.now().toString(36)}-${(counter += 1)}`;

const TONES: Record<
  ToastTone,
  { border: string; text: string; icon: (props: { size?: number }) => JSX.Element }
> = {
  success: { border: 'border-l-up', text: 'text-up', icon: Check },
  error: { border: 'border-l-down', text: 'text-down', icon: AlertTriangle },
  warn: { border: 'border-l-warn', text: 'text-warn', icon: AlertTriangle },
  info: { border: 'border-l-info', text: 'text-info', icon: Info },
  pending: { border: 'border-l-bull', text: 'text-bull', icon: Spinner },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastRecord[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((input: ToastInput) => {
    const id = nextId();
    setItems((current) => [...current, { ...input, id }]);
    return id;
  }, []);

  const api = useMemo<ToastApi>(() => ({ push, dismiss }), [push, dismiss]);
  const visible = items.slice(0, MAX_VISIBLE);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-relevant="additions text"
        className="pointer-events-none fixed right-3 top-[4.25rem] z-[110] flex w-[min(22rem,calc(100vw-1.5rem))] flex-col gap-2 sm:right-4"
      >
        {visible.map((toast) => (
          <ToastRow key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (ctx) return ctx;
  // Rendering outside the provider is a wiring bug, not a reason to crash a page.
  return {
    push: () => {
      if (import.meta.env.DEV) console.warn('useToast(): no <ToastProvider> above this component');
      return '';
    },
    dismiss: () => {},
  };
}

function ToastRow({
  toast,
  onDismiss,
}: {
  toast: ToastRecord;
  onDismiss: (id: string) => void;
}) {
  const tone = TONES[toast.tone ?? 'info'];
  const ToneIcon = tone.icon;
  const lifespan = toast.tone === 'pending' ? 0 : (toast.duration ?? DEFAULT_DURATION);

  const remaining = useRef(lifespan);
  const startedAt = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const resume = useCallback(() => {
    if (lifespan <= 0) return;
    clear();
    startedAt.current = Date.now();
    timer.current = window.setTimeout(
      () => onDismiss(toast.id),
      Math.max(0, remaining.current),
    );
  }, [clear, lifespan, onDismiss, toast.id]);

  const pause = useCallback(() => {
    if (lifespan <= 0) return;
    if (startedAt.current !== null) {
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
      startedAt.current = null;
    }
    clear();
  }, [clear, lifespan]);

  useEffect(() => {
    resume();
    return clear;
  }, [resume, clear]);

  return (
    <div
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      className={cn(
        'pointer-events-auto flex animate-slide-in-right items-start gap-2.5 rounded-lg border border-line border-l-2 bg-overlay p-3 shadow-pop',
        tone.border,
      )}
    >
      <span className={cn('mt-px inline-flex shrink-0', tone.text)}>
        <ToneIcon size={16} />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-xs font-semibold leading-snug text-fg">{toast.title}</p>
        {toast.description ? (
          <p className="break-words text-2xs leading-relaxed text-muted">{toast.description}</p>
        ) : null}

        {toast.txHash || toast.action ? (
          <div className="mt-0.5 flex items-center gap-3">
            {toast.txHash ? (
              <a
                href={explorerUrl(toast.chainId ?? 84532, 'tx', toast.txHash)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 text-2xs font-medium text-bull transition-colors duration-150 ease-swift hover:text-bull-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70"
              >
                View on explorer
                <ArrowUpRight size={12} />
              </a>
            ) : null}
            {toast.action ? (
              <button
                type="button"
                onClick={toast.action.onClick}
                className="text-2xs font-medium text-muted underline decoration-line-strong underline-offset-2 transition-colors duration-150 ease-swift hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70"
              >
                {toast.action.label}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <IconButton
        label="Dismiss notification"
        size="sm"
        onClick={() => onDismiss(toast.id)}
        className="-mr-1 -mt-1 shrink-0"
      >
        <X size={14} />
      </IconButton>
    </div>
  );
}
